import React, { useRef, useMemo } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { formatNumber } from 'utils/formatter';
import { DividendProps } from 'pages/DividendPrediction';
import { commaFormat } from 'components/features/new-report/utils/formatter';
import { marker } from 'components/features/utils/chartUtils';

type PortfolioRatioChartDatum = {
  key: string;
  ratio: number;
};

export const dataToSeries = (
  data: PortfolioRatioChartDatum[]
): Highcharts.SeriesOptionsType[] => {
  return [
    {
      type: 'pie',
      data: data.map(
        (datum, index): Highcharts.PointOptionsObject => {
          const color = colorList[index];
          const label = datum.key;
          return {
            color,
            name: label,
            y: datum.ratio,
          };
        }
      ),
      dataLabels: {
        enabled: true,
        distance: '30%',
        style: {
          color: '#334155',
          textOutline: 'none',
        },
      },
      minSize: 180,
      center: ['50%', '50%'],
    },
  ];
};

export const DividendPieChartNoData = () => {
  return (
    <div className="flex">
      <img
        src="/static/images/dividend/dividend_pie_chart_no_data.png"
        alt="パイチャートのイメージ"
        className="mx-auto"
        height="255"
        width="255"
      />
    </div>
  );
};

type PieChartDataProps = {
  key: string;
  ratio: number;
};

export const DividendPieChart = ({
  dividendList,
}: {
  dividendList: DividendProps[];
}) => {
  const chartData = useMemo(() => {
    const sortedDividendData = dividendList.sort((a, b) => {
      return a.haito < b.haito ? 1 : -1;
    });
    const totalDividend = sortedDividendData.reduce(
      (acc, v) => acc + v.haito,
      0
    );

    const displayItemIndex = 19;
    if (sortedDividendData.length < displayItemIndex) {
      const chartData = sortedDividendData.map(item => {
        return { key: item.symbolName, ratio: item.haito / totalDividend };
      });

      return chartData;
    } else {
      const targetDividendData = sortedDividendData.filter(
        (_, index) => index < displayItemIndex
      );
      const displayDividenChartData = targetDividendData.map(item => {
        return { key: item.symbolName, ratio: item.haito / totalDividend };
      });

      const otherDividendData = sortedDividendData.filter(
        (_, index) => index >= displayItemIndex
      );
      const otherTotalDividend = otherDividendData.reduce(
        (acc, v) => acc + v.haito,
        0
      );
      const otherIndex = sortedDividendData.length - displayItemIndex;
      const otherDividendChartData = {
        key: `その他（${formatNumber(otherIndex, {})}件）`,
        ratio: otherTotalDividend / totalDividend,
      };
      const chartData = displayDividenChartData.concat(
        otherDividendChartData
      ) as PieChartDataProps[];

      return chartData;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dividendList]);

  const series = dataToSeries(chartData.filter(datum => datum.ratio >= 0));

  const options = {
    exporting: { enabled: false },
    credits: { enabled: false },
    title: {
      text: '',
    },
    subtitle: {
      text: '',
    },
    legend: {
      enabled: true,
      align: 'right',
      verticalAlign: 'middle',
      layout: 'vertical',
      itemMarginTop: 10,
      itemStyle: {
        fontWeight: 'normal',
        fontSize: '10px',
        color: '#ffffff',
      },
    },
    chart: {
      type: 'column',
      height: 255,
    },
    tooltip: {
      outside: true,
      formatter(): string {
        return `${this.point.name}: ${commaFormat(this.point.percentage ?? 0, {
          numberOfDecimals: 1,
        })}%`;
      },
    },
    plotOptions: {
      series: {
        ...marker,
        borderColor: '#ffffff',
        borderWidth: 2,
        turboThreshold: 15000,
      },
      pie: {
        innerSize: '50%',
        dataLabels: {
          enabled: true,
          format: '<b>{point.name}</b>',
          style: {
            fontWeight: 'normal',
          },
        },
      },
    },
    series,
  } as Highcharts.Options;

  const ref = useRef<HighchartsReact.RefObject>(null);
  return (
    <>
      <HighchartsReact highcharts={Highcharts} options={options} ref={ref} />
    </>
  );
};

const colorList = [
  '#7C6BE5',
  '#A1E56B',
  '#E56BB5',
  '#31C6CF',
  '#2DB897',
  '#F1894F',
  '#CF3144',
  '#3E739A',
  '#6D7E98',
  '#A17592',
  '#37C681',
  '#3196CF',
  '#BA6FD4',
  '#CF4D31',
  '#FFC738',
  '#7E81CD',
  '#D48888',
  '#CF317D',
  '#548989',
  '#9F9E95',
];
